import { useRenewAuthorizationToken } from '@squareup/dex-store-access-shared-main-authorization-token-hooks';
import { SandboxAuthorization } from '@squareup/dex-types-shared-sandbox-test-account';
import { Box, Heading30, Paragraph30 } from '@squareup/dex-ui-shared-base';
import {
  MarketButton,
  MarketButtonGroup,
  MarketDialog,
  MarketFooter,
  MarketHeader,
  useToaster,
} from '@squareup/dex-ui-shared-market';
import React, { FC, useCallback, useState } from 'react';

import styles from './sandbox-auth-renewal-dialog.module.css';

const strings = {
  title: 'Renew access token',
  description: "Renew this application's access token for the test account",
  descContinued: 'The access token value will not change.',
  renewToken: 'Renew token',
  cancel: 'Cancel',
  toastSuccess: 'Access token renewed.',
  toastFailure: 'Something went wrong. Please try again at a later time.',
};

type Props = {
  authorization: SandboxAuthorization;
  merchantName: string;
  onClose(): void;
  onRenew(): void;
};

const SandboxAuthorizationRenewalDialog: FC<Props> = ({
  authorization,
  merchantName,
  onRenew,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToaster();

  const { renewToken } = useRenewAuthorizationToken();

  const handleRenew = useCallback(async () => {
    setIsLoading(true);
    try {
      await renewToken(authorization);
      showToast({
        message: strings['toastSuccess'],
        variant: 'success',
      });
      onRenew();
      onClose();
    } catch {
      showToast({
        message: strings['toastFailure'],
        variant: 'critical',
      });
      onClose();
    }
  }, [authorization, onClose, onRenew, renewToken, showToast]);

  return (
    <MarketDialog
      className={styles['dialog'] as string}
      onMarketDialogDismissed={onClose}
    >
      <MarketHeader>
        <Heading30>{strings['title']}</Heading30>
      </MarketHeader>

      <Box margin={{ bottom: '2x' }}>
        <Paragraph30>
          {strings['description']}{' '}
          <Paragraph30 as="span" weight="semi-bold">
            {merchantName}
          </Paragraph30>
          .
        </Paragraph30>
        <Paragraph30>{strings['descContinued']}</Paragraph30>
      </Box>

      <MarketFooter>
        <MarketButtonGroup alignment="fill">
          <MarketButton
            isLoading={isLoading}
            onClick={handleRenew}
            rank="primary"
            trackingId="sandbox-auth-renew-confirm-button"
          >
            {strings['renewToken']}
          </MarketButton>
          <MarketButton
            onClick={onClose}
            rank="secondary"
            trackingId="sandbox-auth-renew-cancel-button"
          >
            {strings['cancel']}
          </MarketButton>
        </MarketButtonGroup>
      </MarketFooter>
    </MarketDialog>
  );
};

export default SandboxAuthorizationRenewalDialog;
